import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import WaitlistHeader from './WaitlistHeader';
import './WhitepaperPage.css';

const SECTION_IDS = ['problem', 'vision', 'architecture', 'brain', 'earn', 'roadmap', 'join'] as const;
const SECTION_NUMS = ['01', '02', '03', '04', '05', '06', '07'];
const SECTION_TOC_KEYS = ['toc_problem', 'toc_vision', 'toc_architecture', 'toc_brain', 'toc_earn', 'toc_roadmap', 'toc_join'];

const WhitepaperPage: React.FC = () => {
    const { t } = useTranslation('marketing');
    const [progress, setProgress] = useState(0);
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
            { threshold: 0.1 }
        );
        document.querySelectorAll('.wp-reveal').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id: string) => {
        const el = sectionRefs.current[id];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const ref = (id: string) => (el: HTMLElement | null) => { sectionRefs.current[id] = el; };

    return (
        <div className="whitepaper-root min-h-screen flex flex-col overflow-x-hidden relative bg-hig-bg-primary">
            {/* ── Background — identical to MarketingSite, dark + light aware ── */}
            <div className="fixed inset-0 z-0 overflow-hidden bg-hig-bg-primary">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:opacity-100 opacity-0" />
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 dark:opacity-0 opacity-100" />
                <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]" />
            </div>

            <div className="whitepaper-progress" style={{ width: `${progress}%` }} />

            <WaitlistHeader />

            {/* ════════════════ HERO ════════════════ */}
            <section className="wp-hero">
                <span className="wp-hero-eyebrow">{t('whitepaper.hero_eyebrow')}</span>
                <h1>
                    {t('whitepaper.hero_h1_main')}<br />
                    <em>{t('whitepaper.hero_h1_accent')}</em>
                </h1>
                <p className="wp-hero-sub">{t('whitepaper.hero_sub')}</p>

                <div className="wp-proof-row">
                    <div className="wp-proof-badge">
                        <div className="wp-proof-num">175+</div>
                        <div className="wp-proof-label" style={{ whiteSpace: 'pre-line' }}>{t('whitepaper.hero_badge_patent_label')}</div>
                    </div>
                    <div className="wp-proof-badge">
                        <div className="wp-proof-num">EU</div>
                        <div className="wp-proof-label" style={{ whiteSpace: 'pre-line' }}>{t('whitepaper.hero_badge_eu_label')}</div>
                    </div>
                    <div className="wp-proof-badge">
                        <div className="wp-proof-num">24/7</div>
                        <div className="wp-proof-label" style={{ whiteSpace: 'pre-line' }}>{t('whitepaper.hero_badge_24h_label')}</div>
                    </div>
                </div>

                <div className="wp-hero-actions">
                    <a href="#join" className="wp-btn-primary">{t('whitepaper.hero_cta_primary')}</a>
                    <a href="#problem" className="wp-btn-ghost">{t('whitepaper.hero_cta_ghost')}</a>
                </div>
            </section>

            {/* ════════════════ TABLE OF CONTENTS ════════════════ */}
            <div className="wp-toc">
                <div className="wp-toc-grid">
                    {SECTION_IDS.map((id, i) => (
                        <button key={id} className="wp-toc-item" onClick={() => scrollTo(id)}>
                            <span className="wp-toc-num">{SECTION_NUMS[i]}</span>
                            <span className="wp-toc-label">{t(`whitepaper.${SECTION_TOC_KEYS[i]}`)}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ════════════ 01 — THE PROBLEM ════════════ */}
            <section ref={ref('problem')} id="problem" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s01_num')}</span>
                <h2>{t('whitepaper.s01_h2_main')}<em>{t('whitepaper.s01_h2_em')}</em></h2>
                <p>{t('whitepaper.s01_p1')}</p>
                <p>{t('whitepaper.s01_p2')}</p>
                <p>{t('whitepaper.s01_p3_main')}<strong>{t('whitepaper.s01_p3_strong')}</strong></p>
                <div className="wp-pullquote">
                    <p>{t('whitepaper.s01_pullquote')}</p>
                </div>
            </section>

            {/* ════════════ 02 — A BETTER WAY ════════════ */}
            <section ref={ref('vision')} id="vision" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s02_num')}</span>
                <h2>{t('whitepaper.s02_h2_main')}<em>{t('whitepaper.s02_h2_em')}</em>{t('whitepaper.s02_h2_end')}</h2>
                <p>{t('whitepaper.s02_p1')}</p>
                <p>{t('whitepaper.s02_p2_main')}<strong>{t('whitepaper.s02_p2_strong')}</strong></p>
                <p>{t('whitepaper.s02_p3')}</p>
                <div className="wp-pullquote">
                    <p>{t('whitepaper.s02_pullquote')}</p>
                </div>
            </section>

            {/* ════════════ 03 — HOW IT WORKS ════════════ */}
            <section ref={ref('architecture')} id="architecture" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s03_num')}</span>
                <h2>{t('whitepaper.s03_h2_main')}<em>{t('whitepaper.s03_h2_em')}</em></h2>
                <p>{t('whitepaper.s03_intro')}</p>
                <div className="wp-pillars">
                    <div className="wp-pillar">
                        <div className="wp-pillar-icon">01</div>
                        <div>
                            <h3>{t('whitepaper.s03_p1_h3')}</h3>
                            <p>{t('whitepaper.s03_p1_body')}</p>
                        </div>
                    </div>
                    <div className="wp-pillar">
                        <div className="wp-pillar-icon">02</div>
                        <div>
                            <h3>{t('whitepaper.s03_p2_h3')}</h3>
                            <p>{t('whitepaper.s03_p2_body')}</p>
                        </div>
                    </div>
                    <div className="wp-pillar">
                        <div className="wp-pillar-icon">03</div>
                        <div>
                            <h3>{t('whitepaper.s03_p3_h3')}</h3>
                            <p>{t('whitepaper.s03_p3_body')}</p>
                        </div>
                    </div>
                </div>
                <p>{t('whitepaper.s03_closing')}</p>
            </section>

            {/* ════════════ 04 — BUILT LIKE A BRAIN ════════════ */}
            <section ref={ref('brain')} id="brain" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s04_num')}</span>
                <h2>{t('whitepaper.s04_h2_main')}<br /><em>{t('whitepaper.s04_h2_em')}</em></h2>
                <p>{t('whitepaper.s04_p1')}</p>
                <p>{t('whitepaper.s04_p2')}</p>
                <div className="wp-forces">
                    <div className="wp-force">
                        <div className="wp-force-num">01</div>
                        <h3>{t('whitepaper.s04_f1_h3')}</h3>
                        <p>{t('whitepaper.s04_f1_body')}</p>
                    </div>
                    <div className="wp-force">
                        <div className="wp-force-num">02</div>
                        <h3>{t('whitepaper.s04_f2_h3')}</h3>
                        <p>{t('whitepaper.s04_f2_body')}</p>
                    </div>
                    <div className="wp-force">
                        <div className="wp-force-num">03</div>
                        <h3>{t('whitepaper.s04_f3_h3')}</h3>
                        <p>{t('whitepaper.s04_f3_body')}</p>
                    </div>
                </div>
                <p>{t('whitepaper.s04_closing')}</p>
            </section>

            {/* ════════════ 05 — YOUR REPLICA EARNS ════════════ */}
            <section ref={ref('earn')} id="earn" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s05_num')}</span>
                <h2>{t('whitepaper.s05_h2_main')}<em>{t('whitepaper.s05_h2_em')}</em>{t('whitepaper.s05_h2_end') && <><br />{t('whitepaper.s05_h2_end')}</>}</h2>
                <p>{t('whitepaper.s05_p1')}</p>
                {(t('whitepaper.s05_p2_main') || t('whitepaper.s05_p2_strong')) && (
                    <p>{t('whitepaper.s05_p2_main')}<strong>{t('whitepaper.s05_p2_strong')}</strong></p>
                )}
                {t('whitepaper.s05_p3') && <p>{t('whitepaper.s05_p3')}</p>}
                {t('whitepaper.s05_p4') && <p>{t('whitepaper.s05_p4')}</p>}
                <div className="wp-pullquote">
                    <p>{t('whitepaper.s05_pullquote')}</p>
                </div>
            </section>

            {/* ════════════ 06 — WHAT'S COMING ════════════ */}
            <section ref={ref('roadmap')} id="roadmap" className="wp-section wp-reveal">
                <span className="wp-section-num">{t('whitepaper.s06_num')}</span>
                <h2>{t('whitepaper.s06_h2_main')}<em>{t('whitepaper.s06_h2_em')}</em></h2>
                <p>{t('whitepaper.s06_intro')}</p>
                <div className="wp-roadmap">
                    <div className="wp-phase">
                        <div className="wp-phase-time">
                            {t('whitepaper.s06_ph1_time')}<span className="wp-phase-status">{t('whitepaper.s06_ph1_status')}</span>
                        </div>
                        <div>
                            <h3>{t('whitepaper.s06_ph1_h3')}</h3>
                            <p>{t('whitepaper.s06_ph1_body')}</p>
                        </div>
                    </div>
                    <div className="wp-phase">
                        <div className="wp-phase-time">
                            {t('whitepaper.s06_ph2_time')}<span className="wp-phase-status">{t('whitepaper.s06_ph2_status')}</span>
                        </div>
                        <div>
                            <h3>{t('whitepaper.s06_ph2_h3')}</h3>
                            <p>{t('whitepaper.s06_ph2_body')}</p>
                        </div>
                    </div>
                    <div className="wp-phase">
                        <div className="wp-phase-time">
                            {t('whitepaper.s06_ph3_time')}<span className="wp-phase-status">{t('whitepaper.s06_ph3_status')}</span>
                        </div>
                        <div>
                            <h3>{t('whitepaper.s06_ph3_h3')}</h3>
                            <p>{t('whitepaper.s06_ph3_body')}</p>
                        </div>
                    </div>
                    <div className="wp-phase">
                        <div className="wp-phase-time">
                            {t('whitepaper.s06_ph4_time')}<span className="wp-phase-status">{t('whitepaper.s06_ph4_status')}</span>
                        </div>
                        <div>
                            <h3>{t('whitepaper.s06_ph4_h3')}</h3>
                            <p>{t('whitepaper.s06_ph4_body')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ════════════ 07. GET INVOLVED ════════════ */}
            <section ref={ref('join')} id="join" className="wp-cta-section wp-reveal">
                <h2>{t('whitepaper.s07_h2_main')}<br />{t('whitepaper.s07_h2_end')}<em>{t('whitepaper.s07_h2_em')}</em></h2>
                <p>{t('whitepaper.s07_p1')}</p>
                <p>{t('whitepaper.s07_p2')}</p>
                <a href="/" className="wp-btn-primary">{t('whitepaper.s07_cta')}</a>
                <span className="wp-cta-closing">{t('whitepaper.s07_closing')}</span>
            </section>

            {/* ════════════ MANIFESTO ════════════ */}
            <div className="wp-manifesto">
                <p>{t('whitepaper.manifesto')}</p>
            </div>

            {/* ════════════ FOOTER ════════════ */}
            <footer className="wp-footer">
                <div className="wp-footer-left">
                    {t('whitepaper.footer_left')}
                </div>
                <div className="wp-footer-social">
                    <a href="https://x.com/humanreplica_ai" target="_blank" rel="noopener noreferrer" aria-label="X">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                    <a href="https://linkedin.com/company/humanreplica-ai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                </div>
                <div className="wp-footer-right">
                    <a href="/whitepaper">{t('manifesto.footer_whitepaper')}</a>
                    <a href="/privacy">{t('manifesto.footer_privacy')}</a>
                </div>
            </footer>
        </div>
    );
};

export default WhitepaperPage;
